import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import {
  Activity, AlertTriangle, CheckCircle2, RefreshCw, Database, Download, Clock, ExternalLink, ChevronDown, ChevronUp,
} from 'lucide-react';

function fmt(n) { return (n || 0).toLocaleString('en-IN'); }
function money(n) { return `₹${fmt(n)}`; }

export default function SystemHealthPage() {
  const { showToast } = useToast();
  const [health, setHealth] = useState(null);
  const [errors, setErrors] = useState([]);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningCheck, setRunningCheck] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [healthRes, errorsRes, backupsRes] = await Promise.all([
        api.get('/system/health'),
        api.get('/system/errors'),
        api.get('/system/backups'),
      ]);
      setHealth(healthRes.data);
      setErrors(errorsRes.data.errors || []);
      setBackups(backupsRes.data.backups || []);
    } catch (err) {
      showToast('Could not load system health data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function runNow() {
    setRunningCheck(true);
    try {
      await api.post('/system/health/run-now');
      showToast('Health check complete.', 'success');
      loadAll();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not run the health check.', 'error');
    } finally {
      setRunningCheck(false);
    }
  }

  async function downloadBackup(filename) {
    try {
      const token = sessionStorage.getItem('dq_token');
      const res = await fetch(`/api/system/backups/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed.');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast('Could not download this backup.', 'error');
    }
  }

  const latest = health?.latest;

  return (
    <AppLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>System Health</h1>
          <p style={{ fontSize: 13, color: 'var(--color-ink-faint)', margin: '4px 0 0' }}>
            Billing checks, server errors, and automatic backups — all in one place, no shell access needed.
          </p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={loadAll}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="skeleton" style={{ height: 200, borderRadius: 10 }} />
      ) : (
        <>
          {/* Billing health check */}
          <div className="card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Activity size={17} color="var(--color-primary)" />
                <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Billing Health Check</h3>
              </div>
              <button className="btn btn-primary btn-sm" onClick={runNow} disabled={runningCheck}>
                {runningCheck ? 'Running…' : 'Run Now'}
              </button>
            </div>

            {!latest ? (
              <div style={{ fontSize: 13, color: 'var(--color-ink-faint)', fontStyle: 'italic' }}>
                No health check has run yet — click "Run Now" or wait for the automatic weekly check (Sundays).
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 13, color: 'var(--color-ink-faint)' }}>
                  <Clock size={13} />
                  Last checked {new Date(latest.createdAt).toLocaleString('en-IN')} · triggered by {latest.triggeredBy}
                </div>
                <HealthCheckCategories latest={latest} onRunNow={runNow} runningCheck={runningCheck} />
                {latest.totalIssues === 0 ? (
                  <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-success)', fontSize: 13, fontWeight: 600 }}>
                    <CheckCircle2 size={16} /> No issues found on the last check.
                  </div>
                ) : (
                  <div style={{ marginTop: 14, fontSize: 13, color: 'var(--color-warning)' }}>
                    ⚠ {latest.totalIssues} issue(s) found — click a category above to see which patients and amounts are involved. Run{' '}
                    <code>scripts/dedupe-doctor-cost-expenses.js</code> for duplicate doctor costs.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Backup status */}
          <div className="card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Database size={17} color="var(--color-primary)" />
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Automatic Backups</h3>
              <span style={{ fontSize: 12, color: 'var(--color-ink-faint)' }}>— daily at 2:00 AM, last 14 kept</span>
            </div>
            {backups.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--color-ink-faint)', fontStyle: 'italic' }}>
                No automatic backups yet — the first one runs at 2:00 AM server time.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {backups.map(b => (
                  <div key={b.filename} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
                    <span style={{ flex: 1 }}>{b.filename}</span>
                    <span style={{ color: 'var(--color-ink-faint)' }}>{(b.sizeBytes / 1024 / 1024).toFixed(1)} MB</span>
                    <span style={{ color: 'var(--color-ink-faint)' }}>{new Date(b.createdAt).toLocaleString('en-IN')}</span>
                    <button className="btn btn-outline btn-sm" style={{ padding: '3px 8px' }} onClick={() => downloadBackup(b.filename)}>
                      <Download size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error log */}
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <AlertTriangle size={17} color="var(--color-danger)" />
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Recent Server Errors</h3>
            </div>
            {errors.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-success)', fontSize: 13, fontWeight: 600 }}>
                <CheckCircle2 size={16} /> No errors logged.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {errors.map(e => (
                  <ErrorLogEntry key={e._id} error={e} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </AppLayout>
  );
}

function ErrorLogEntry({ error: e }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div style={{ padding: '10px 12px', background: 'var(--color-danger-soft)', borderRadius: 8, fontSize: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, gap: 10 }}>
        <span style={{ fontWeight: 700, color: 'var(--color-danger)' }}>{e.source}</span>
        <span style={{ color: 'var(--color-ink-faint)', flexShrink: 0 }}>{new Date(e.createdAt).toLocaleString('en-IN')}</span>
      </div>
      <div>{e.message}</div>
      {e.path && <div style={{ color: 'var(--color-ink-faint)', marginTop: 2 }}>{e.method} {e.path}</div>}
      {e.stack && (
        <>
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            style={{
              marginTop: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: 'var(--color-danger)', fontSize: 11, fontWeight: 700, textDecoration: 'underline',
            }}
          >
            {expanded ? 'Hide details' : 'Show details'}
          </button>
          {expanded && (
            <pre style={{
              marginTop: 8, padding: '10px 12px', background: 'var(--color-bg)', borderRadius: 6,
              fontSize: 11, lineHeight: 1.5, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              border: '1px solid var(--color-border)', color: 'var(--color-ink-soft)',
            }}>
              {e.stack}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

// The 4 clickable summary cards for Billing Health Check — clicking one
// with a count > 0 expands a detail table below showing exactly which
// patient(s) and what the payment numbers are, instead of just a bare
// count with nowhere to go look further.
function HealthCheckCategories({ latest, onRunNow, runningCheck }) {
  const [expandedKey, setExpandedKey] = React.useState(null);

  const categories = [
    {
      key: 'overpaidTreatment', label: 'Overpaid Treatment', count: latest.overpaidTreatment,
      rows: latest.details?.overpaidTreatment,
      columns: [
        ['patient', 'Patient'], ['uid', 'UID'], ['visit', 'Visit'],
        ['billed', 'Billed', money], ['paid', 'Paid', money], ['gap', 'Overpaid by', money],
      ],
      getFixLink: (row) => `/doctor/billing?tab=treatment&search=${encodeURIComponent(row.uid)}`,
    },
    {
      key: 'overpaidPharmacy', label: 'Overpaid Pharmacy', count: latest.overpaidPharmacy,
      rows: latest.details?.overpaidPharmacy,
      columns: [
        ['uid', 'UID'], ['owed', 'Owed', money], ['paid', 'Paid', money], ['gap', 'Overpaid by', money],
      ],
      getFixLink: (row) => `/doctor/billing?tab=pharmacy&search=${encodeURIComponent(row.uid)}`,
    },
    {
      key: 'duplicateDoctorCost', label: 'Duplicate Doctor Costs', count: latest.duplicateDoctorCost,
      rows: latest.details?.duplicateDoctorCost,
      columns: [
        ['patient', 'Patient'], ['uid', 'UID'], ['amount', 'Amount', money], ['count', 'Duplicate entries'],
      ],
    },
    {
      key: 'unbilledPharmacy', label: 'Unbilled Pharmacy', count: latest.unbilledPharmacyCount,
      rows: latest.details?.unbilledPharmacyPatients,
      columns: [
        ['patient', 'Patient'], ['uid', 'UID'], ['gross', 'Purchases', money],
      ],
    },
    {
      key: 'totalPending', label: 'Total Pending', count: latest.totalPendingCount,
      rows: latest.details?.totalPendingPatients,
      informational: true, // not a data problem — just accounts receivable
      subtitle: latest.totalPendingAmount > 0 ? money(latest.totalPendingAmount) : null,
      columns: [
        ['patient', 'Patient'], ['uid', 'UID'], ['visits', 'Visits w/ balance'], ['pending', 'Pending', money],
      ],
    },
  ];

  const active = categories.find((c) => c.key === expandedKey);

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        {categories.map((c) => {
          const clickable = c.count > 0;
          const isOpen = expandedKey === c.key;
          const accentColor = c.informational ? 'var(--color-primary)' : 'var(--color-warning)';
          const accentSoft = c.informational ? 'var(--color-primary-soft)' : 'var(--color-warning-soft)';
          return (
            <div key={c.key} className="tilt3d-scene">
            <button
              type="button"
              onClick={() => clickable && setExpandedKey(isOpen ? null : c.key)}
              disabled={!clickable}
              className="tilt3d-card"
              style={{
                textAlign: 'left', padding: '12px 14px', borderRadius: 8, border: isOpen ? `2px solid ${accentColor}` : '2px solid transparent',
                background: c.count > 0 ? accentSoft : 'var(--color-surface)',
                cursor: clickable ? 'pointer' : 'default',
                width: '100%',
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', fontWeight: 700, textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between' }}>
                {c.label}
                {clickable && <span style={{ color: accentColor }}>{isOpen ? '▲' : '▼'}</span>}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: c.count > 0 ? accentColor : 'var(--color-ink)' }}>{fmt(c.count)}</div>
              {c.subtitle && <div style={{ fontSize: 12, color: 'var(--color-ink-faint)', marginTop: 2 }}>{c.subtitle}</div>}
            </button>
            </div>
          );
        })}
      </div>

      {active && (
        <HealthCheckDetailTable category={active} onClose={() => setExpandedKey(null)} onRunNow={onRunNow} runningCheck={runningCheck} />
      )}
    </>
  );
}

function HealthCheckDetailTable({ category, onClose, onRunNow, runningCheck }) {
  const rows = category.rows || [];
  const accentColor = category.informational ? 'var(--color-primary)' : 'var(--color-warning)';
  const accentSoft = category.informational ? 'var(--color-primary-soft)' : 'var(--color-warning-soft)';
  const navigate = useNavigate();
  const [expandedRow, setExpandedRow] = React.useState(null);

  return (
    <div className="card" style={{ marginTop: 12, padding: 0, overflow: 'hidden', border: `1px solid ${accentColor}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: accentSoft }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: accentColor }}>{category.label} — {rows.length} record{rows.length !== 1 ? 's' : ''}</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--color-ink-faint)', marginBottom: 10 }}>
            This count is from a health check run before per-record detail was tracked, so there's nothing to list here yet.
            Run a fresh check to populate the details for this and future runs.
          </div>
          <button className="btn btn-primary btn-sm" onClick={onRunNow} disabled={runningCheck}>
            {runningCheck ? 'Running…' : 'Run Now'}
          </button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                {category.columns.map(([key, label]) => (
                  <th key={key} style={{ textAlign: 'left', padding: '8px 14px', color: 'var(--color-ink-faint)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{label}</th>
                ))}
                {(rows.some((r) => r.paymentHistory) || category.getFixLink) && (
                  <th style={{ padding: '8px 14px' }} />
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const hasHistory = row.paymentHistory && row.paymentHistory.length > 0;
                const isExpanded = expandedRow === i;
                return (
                  <React.Fragment key={i}>
                    <tr style={{ borderTop: i > 0 ? '1px solid var(--color-border)' : 'none' }}>
                      {category.columns.map(([key, label, formatter]) => (
                        <td key={key} style={{ padding: '8px 14px', fontWeight: key === 'gap' ? 700 : 400, color: key === 'gap' ? 'var(--color-danger)' : 'var(--color-ink)' }}>
                          {formatter ? formatter(row[key]) : (row[key] ?? '—')}
                        </td>
                      ))}
                      {(rows.some((r) => r.paymentHistory) || category.getFixLink) && (
                        <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            {hasHistory && (
                              <button
                                className="btn btn-ghost btn-sm"
                                style={{ padding: '3px 8px', fontSize: 11 }}
                                onClick={() => setExpandedRow(isExpanded ? null : i)}
                              >
                                {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Payments
                              </button>
                            )}
                            {category.getFixLink && (
                              <button
                                className="btn btn-outline btn-sm"
                                style={{ padding: '3px 8px', fontSize: 11 }}
                                onClick={() => navigate(category.getFixLink(row))}
                              >
                                <ExternalLink size={12} /> Fix
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    {isExpanded && hasHistory && (
                      <tr>
                        <td colSpan={category.columns.length + 1} style={{ padding: '0 14px 12px', background: 'var(--color-bg)' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-ink-faint)', textTransform: 'uppercase', margin: '8px 0 6px' }}>
                            Full Payment History
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--color-ink-faint)', fontWeight: 700 }}>Date</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--color-ink-faint)', fontWeight: 700 }}>Amount</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--color-ink-faint)', fontWeight: 700 }}>Method</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--color-ink-faint)', fontWeight: 700 }}>Note</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--color-ink-faint)', fontWeight: 700 }}>Recorded By</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.paymentHistory.map((h, hi) => (
                                <tr key={hi} style={{ borderTop: '1px solid var(--color-border)' }}>
                                  <td style={{ padding: '4px 8px' }}>{h.paidAt ? new Date(h.paidAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                  <td style={{ padding: '4px 8px', fontWeight: 700 }}>{money(h.amount)}</td>
                                  <td style={{ padding: '4px 8px' }}>{h.method || '—'}</td>
                                  <td style={{ padding: '4px 8px', color: 'var(--color-ink-faint)' }}>{h.note || '—'}</td>
                                  <td style={{ padding: '4px 8px', color: 'var(--color-ink-faint)' }}>{h.recordedBy || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
